import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Lock, RefreshCcw, Phone, MessageSquare, Sparkles, MapPin, Users, MessageCircle, Eye, Calendar, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { LOGO_URL } from '../lib/constants';
import {
  adminLogin,
  adminLogout,
  getAdminToken,
  fetchAdminDashboard,
  fetchAdminChats,
  fetchAdminChatTranscript,
  fetchAdminLeads,
} from '../lib/api';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 Days' },
  { key: 'month', label: '30 Days' },
  { key: 'all', label: 'All Time' },
];

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="panel rounded-2xl p-4 sm:p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-white/60">{label}</div>
      {Icon && <Icon className="h-4 w-4 text-[color:var(--neon-red)]" />}
    </div>
    <div className="mt-2 font-display text-2xl sm:text-3xl text-white tracking-tight">{value}</div>
    {sub && <div className="mt-1 text-xs text-white/50">{sub}</div>}
  </div>
);

const LoginScreen = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      await adminLogin(password);
      toast.success('Welcome back');
      onSuccess();
    } catch (e) {
      toast.error('Wrong password. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#07080A]">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm panel rounded-2xl p-6 border border-[color:var(--neon-red)]/30"
        style={{ boxShadow: '0 0 0 1px rgba(255,42,42,0.35), 0 30px 80px rgba(0,0,0,0.6)' }}
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full overflow-hidden glow-red-sm">
            <img src={LOGO_URL} alt="" className="h-full w-full object-cover" />
          </div>
          <div>
            <div className="font-display uppercase tracking-[0.18em] text-[10px] text-white/60">Universal Kempo</div>
            <div className="font-display text-white text-lg tracking-tight">Admin Access</div>
          </div>
        </div>
        <div className="mt-6">
          <Label className="text-white/70 text-xs uppercase tracking-[0.14em]">Password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/40"
            data-testid="admin-password-input"
            autoFocus
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="mt-5 w-full bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] rounded-xl py-5 glow-red-md"
          data-testid="admin-login-button"
        >
          <Lock className="h-4 w-4 mr-2" /> Enter
        </Button>
        <div className="mt-3 text-center text-[10px] font-mono-ui text-white/40 uppercase tracking-[0.16em]">
          Private area — authorized users only
        </div>
      </motion.form>
    </div>
  );
};

const Dashboard = () => {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [chats, setChats] = useState([]);
  const [transcript, setTranscript] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, ch, lds] = await Promise.all([
        fetchAdminDashboard(period),
        fetchAdminChats(50),
        fetchAdminLeads(500),
      ]);
      setData(dash);
      setChats(ch);
      setLeads(lds);
    } catch (e) {
      if (e?.response?.status === 401) {
        adminLogout();
        window.location.reload();
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const openTranscript = async (sid) => {
    try {
      const t = await fetchAdminChatTranscript(sid);
      setTranscript(t);
    } catch (e) {
      toast.error('Could not load transcript');
    }
  };

  const s = data?.summary || {};
  const timeseries = data?.timeseries || [];
  const maxViews = Math.max(1, ...timeseries.map((t) => t.page_view || 0));

  return (
    <div className="min-h-screen bg-[#07080A] text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-white/10 bg-black/70 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full overflow-hidden glow-red-sm">
              <img src={LOGO_URL} alt="" className="h-full w-full object-cover" />
            </div>
            <div>
              <div className="font-display uppercase tracking-[0.18em] text-[10px] text-white/60">Universal Kempo</div>
              <div className="font-display text-sm text-white tracking-tight">Admin Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 hover:bg-white/5 px-3 py-2 text-xs font-display uppercase tracking-[0.14em]"
              data-testid="admin-refresh-button"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={() => { adminLogout(); window.location.reload(); }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 hover:bg-white/5 px-3 py-2 text-xs font-display uppercase tracking-[0.14em]"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Period toggle */}
        <div className="flex flex-wrap items-center gap-1.5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-display uppercase tracking-[0.14em] border transition-colors ${
                period === p.key
                  ? 'bg-[color:var(--neon-red)] border-[color:var(--neon-red)] text-white glow-red-sm'
                  : 'border-white/15 text-white/70 hover:bg-white/5'
              }`}
              data-testid={`admin-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="overview" className="font-display uppercase tracking-[0.14em] text-xs data-[state=active]:bg-[color:var(--neon-red)] data-[state=active]:text-white" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="leads" className="font-display uppercase tracking-[0.14em] text-xs data-[state=active]:bg-[color:var(--neon-red)] data-[state=active]:text-white" data-testid="admin-tab-leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="chats" className="font-display uppercase tracking-[0.14em] text-xs data-[state=active]:bg-[color:var(--neon-red)] data-[state=active]:text-white" data-testid="admin-tab-chats">Chats ({chats.length})</TabsTrigger>
          </TabsList>

          {/* --- Overview --- */}
          <TabsContent value="overview" className="mt-5 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <StatCard icon={Eye} label="Unique Visitors" value={s.unique_visitors ?? 0} sub={`${s.sessions ?? 0} sessions`} />
              <StatCard icon={Users} label="Page Views" value={s.page_views ?? 0} />
              <StatCard icon={Phone} label="Phone Clicks" value={s.phone_clicks ?? 0} sub={`${s.text_clicks ?? 0} text‑us clicks`} />
              <StatCard icon={Sparkles} label="Trial Clicks" value={s.trial_clicks ?? 0} sub="Book Free Trial" />
              <StatCard icon={MessageSquare} label="Chat Sessions" value={s.chat_sessions ?? 0} sub={`${s.chat_messages_sent ?? 0} msgs sent`} />
              <StatCard icon={MessageCircle} label="Chat Opens" value={s.chat_opens ?? 0} />
              <StatCard icon={MapPin} label="Directions Clicks" value={s.directions_clicks ?? 0} />
              <StatCard icon={Calendar} label="Leads Submitted" value={s.leads_submitted ?? 0} sub="Free trial forms" />
            </div>

            {/* Daily bar chart */}
            <div className="panel rounded-2xl p-5" data-testid="admin-timeseries">
              <div className="flex items-center justify-between">
                <div className="font-display uppercase tracking-[0.18em] text-[11px] text-white/70">Daily activity</div>
                <div className="font-mono-ui text-[10px] text-[color:var(--neon-cyan)] uppercase tracking-[0.16em]">page views · trials · calls</div>
              </div>
              {timeseries.length === 0 ? (
                <div className="mt-4 text-sm text-white/55">No activity in this window yet. Once your site is live, this graph fills up in real time.</div>
              ) : (
                <div className="mt-5 flex items-end gap-2 h-40 overflow-x-auto">
                  {timeseries.map((t) => (
                    <div key={t.date} className="flex flex-col items-center min-w-[52px]">
                      <div className="relative w-full flex items-end justify-center gap-0.5 h-32">
                        <div className="w-2.5 rounded-t bg-[color:var(--neon-red)]/85" style={{ height: `${(t.page_view / maxViews) * 100}%` }} title={`${t.page_view} views`} />
                        <div className="w-2.5 rounded-t bg-[color:var(--neon-cyan)]/75" style={{ height: `${(t.trial_click / maxViews) * 100}%` }} title={`${t.trial_click} trial clicks`} />
                        <div className="w-2.5 rounded-t bg-white/70" style={{ height: `${(t.phone_click / maxViews) * 100}%` }} title={`${t.phone_click} phone clicks`} />
                      </div>
                      <div className="mt-1 text-[10px] font-mono-ui text-white/50">{t.date.slice(5)}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-white/60 font-mono-ui uppercase tracking-[0.14em]">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[color:var(--neon-red)]" /> Views</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[color:var(--neon-cyan)]" /> Trial Clicks</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-white/70" /> Phone Clicks</span>
              </div>
            </div>

            {/* Recent leads snapshot */}
            <div className="panel rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div className="font-display uppercase tracking-[0.18em] text-[11px] text-white/70">Latest Leads</div>
                <button className="text-[11px] font-display uppercase tracking-[0.14em] text-[color:var(--neon-red)]" onClick={() => setTab('leads')}>See all →</button>
              </div>
              {(data?.leads || []).slice(0, 5).map((l) => (
                <div key={l.id} className="mt-3 rounded-xl border border-white/8 bg-white/[0.02] p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-sm text-white">{l.name}</div>
                    <a href={`tel:${l.phone}`} className="font-mono-ui text-xs text-[color:var(--neon-red)]">{l.phone}</a>
                  </div>
                  <div className="mt-1 text-xs text-white/60">
                    {l.program || 'Program not chosen'} · Age {l.student_age || '—'} · {new Date(l.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
              {(!data?.leads || data.leads.length === 0) && (
                <div className="mt-3 text-sm text-white/55">No leads in this window.</div>
              )}
            </div>
          </TabsContent>

          {/* --- Leads --- */}
          <TabsContent value="leads" className="mt-5">
            <div className="panel rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/60">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-display uppercase tracking-[0.14em] text-[11px]">Name</th>
                    <th className="px-4 py-3 font-display uppercase tracking-[0.14em] text-[11px]">Phone</th>
                    <th className="px-4 py-3 font-display uppercase tracking-[0.14em] text-[11px] hidden sm:table-cell">Program</th>
                    <th className="px-4 py-3 font-display uppercase tracking-[0.14em] text-[11px] hidden md:table-cell">Age</th>
                    <th className="px-4 py-3 font-display uppercase tracking-[0.14em] text-[11px] hidden lg:table-cell">When</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-white/8 hover:bg-white/[0.03]" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-display text-white">{l.name}</div>
                        {l.email && <div className="text-xs text-white/55">{l.email}</div>}
                        {l.message && <div className="mt-1 text-xs text-white/60 italic">“{l.message}”</div>}
                      </td>
                      <td className="px-4 py-3">
                        <a href={`tel:${l.phone}`} className="font-mono-ui text-[color:var(--neon-red)]">{l.phone}</a>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-white/80">{l.program || '—'}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-white/80">{l.student_age || '—'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-white/60 font-mono-ui text-xs">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leads.length === 0 && (
                <div className="p-6 text-center text-white/55">No leads yet. When someone books a free trial, they’ll show up here.</div>
              )}
            </div>
          </TabsContent>

          {/* --- Chats --- */}
          <TabsContent value="chats" className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5 panel rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 font-display uppercase tracking-[0.18em] text-[11px] text-white/70">
                Recent conversations
              </div>
              <div className="max-h-[640px] overflow-y-auto">
                {chats.map((c) => (
                  <button
                    key={c.session_id}
                    onClick={() => openTranscript(c.session_id)}
                    className={`w-full text-left px-4 py-3 border-b border-white/8 hover:bg-white/[0.04] transition-colors ${
                      transcript?.session_id === c.session_id ? 'bg-white/[0.05]' : ''
                    }`}
                    data-testid={`admin-chat-item-${c.session_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-mono-ui text-[10px] text-[color:var(--neon-cyan)] uppercase tracking-[0.16em]">{c.session_id.slice(0, 10)}…</div>
                      <div className="text-[10px] text-white/50 font-mono-ui">{new Date(c.last_ts).toLocaleString()}</div>
                    </div>
                    <div className="mt-1 text-sm text-white/85 line-clamp-2">{c.last_msg}</div>
                    <div className="mt-1 text-[11px] text-white/50">{c.count} msgs</div>
                  </button>
                ))}
                {chats.length === 0 && (
                  <div className="p-6 text-center text-white/55">No chats yet. When visitors talk to Professor AI, they show up here.</div>
                )}
              </div>
            </div>
            <div className="lg:col-span-7 panel rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 font-display uppercase tracking-[0.18em] text-[11px] text-white/70 flex items-center justify-between">
                <span>Transcript</span>
                {transcript && (
                  <span className="font-mono-ui text-[10px] text-[color:var(--neon-cyan)] uppercase tracking-[0.16em]">{transcript.session_id.slice(0, 12)}…</span>
                )}
              </div>
              <div className="p-4 max-h-[640px] overflow-y-auto space-y-2">
                {transcript ? (
                  transcript.messages.map((m, i) => (
                    <div key={`${m.timestamp || 'msg'}-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                        m.role === 'user'
                          ? 'bg-white/10 text-white border border-white/10'
                          : 'bg-[rgba(255,42,42,0.10)] text-white border border-[color:var(--neon-red)]/25'
                      }`}>
                        <div className="font-mono-ui text-[9px] uppercase tracking-[0.18em] text-white/50">{m.role}</div>
                        <div className="mt-0.5 whitespace-pre-wrap">{m.content}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-white/55">Select a conversation on the left to view the full transcript.</div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(!!getAdminToken());
  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;
  return <Dashboard />;
};

export default AdminPage;
